/**
 * NewBusinessPage — /businesses/new
 *
 * Simple form for a signed-in user to spin up a new business page.
 * Minimal fields only — after create we bounce the user straight into
 * the full BusinessEditPage where they can complete the profile.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { UI_INDUSTRIES, labelFor } from "@/data/industries";

export default function NewBusinessPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", industry: "", industry_other: "", city: user?.city || "",
    country: user?.country || "", contact_phone: "", description: "",
  });
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="new-business-signin">
        <Building2 className="w-10 h-10 mx-auto opacity-40 mb-2" />
        <h1 className="font-display text-2xl mb-2">Sign in to add a business</h1>
        <p className="opacity-70 mb-4">You need a Social Pulse account to own a business page.</p>
        <Link to="/login?redirect=/businesses/new" className="wofy-btn-primary inline-flex">Sign in</Link>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter the business name.");
    if (form.industry === "other" && !form.industry_other.trim()) {
      return toast.error("Please specify your industry.");
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (form.industry === "other") {
        payload.industry = form.industry_other.trim().toLowerCase();
      }
      delete payload.industry_other;
      const { data } = await http.post("/businesses", payload);
      toast.success(`${data.business.name} created — complete your profile.`);
      nav(`/business/${data.business.slug}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create business.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="new-business-page">
      <Link to="/businesses" className="text-xs opacity-70 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Business Portal
      </Link>
      <div className="mb-6">
        <div className="overline mb-1">ADD A BUSINESS</div>
        <h1 className="font-display text-4xl">Get your business online</h1>
        <p className="mt-2 opacity-70 max-w-lg">
          Free forever — a public profile with photos, contact info, business hours,
          and reviews. Add a shopping cart whenever you're ready to sell.
        </p>
      </div>

      <div className="wofy-card !p-6 space-y-4">
        <Row label="Business name *">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className="wofy-input" placeholder="e.g. Ammu's Kerala Kitchen" data-testid="nb-name" />
        </Row>
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="Industry">
            <select value={form.industry} onChange={(e) => set("industry", e.target.value)} className="wofy-input" data-testid="nb-industry">
              <option value="">— select —</option>
              {UI_INDUSTRIES.map((i) => <option key={i} value={i}>{labelFor(i)}</option>)}
            </select>
          </Row>
          <Row label="Business phone">
            <input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="wofy-input" placeholder="+91 484 555 0100" data-testid="nb-phone" />
          </Row>
        </div>
        {form.industry === "other" && (
          <Row label="Please specify your industry *">
            <input
              value={form.industry_other}
              onChange={(e) => set("industry_other", e.target.value)}
              className="wofy-input"
              placeholder="e.g. pet grooming, film production, event planning…"
              data-testid="nb-industry-other"
              autoFocus
            />
          </Row>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          <Row label="City">
            <input value={form.city} onChange={(e) => set("city", e.target.value)} className="wofy-input" data-testid="nb-city" />
          </Row>
          <Row label="Country">
            <input value={form.country} onChange={(e) => set("country", e.target.value)} className="wofy-input" data-testid="nb-country" />
          </Row>
        </div>
        <Row label="One-line description">
          <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="wofy-input" placeholder="A warm, home-style Kerala restaurant in Fort Kochi." data-testid="nb-description" />
        </Row>

        <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="text-xs opacity-70 inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> AI can draft the rest for you on the next screen.
          </div>
          <button onClick={submit} disabled={busy} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="nb-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
            Create business
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}
